import React, { useState, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { useModems, useUpdateModem, useDeleteModem, useCreateModem, useAssignModem } from '@/hooks/useModems';
import { IModem, IModemListParams, IModemAssignRequest } from '@/types/api';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { MobileCard } from '@/components/ui';

// Column definition matching Google Sheet exactly
// A=Part Number, B=Serial Number, C=IMEI, D=Firmware, E=Owner,
// F=Company, G=Email, H=Phone, I=Notes, J=Assigned At, K=Installed SIM
const COLUMNS = [
  { key: 'modem_id', label: 'Part Number', editable: false }, // A - Primary key, not editable
  { key: 'serial_number', label: 'Serial Number', editable: true }, // B
  { key: 'imei', label: 'IMEI', editable: true }, // C
  { key: 'firmware_version', label: 'Firmware', editable: true }, // D
  { key: 'owner', label: 'Owner', editable: true }, // E
  { key: 'company', label: 'Company', editable: true }, // F
  { key: 'email', label: 'Email', editable: true }, // G
  { key: 'phone', label: 'Phone', editable: true }, // H
  { key: 'notes', label: 'Notes', editable: true }, // I
  { key: 'assigned_at', label: 'Assigned At', editable: true }, // J
  { key: 'installed_sim', label: 'Installed SIM', editable: true }, // K
] as const;

type ColumnKey = typeof COLUMNS[number]['key'];

interface EditingCell {
  modemId: string;
  field: ColumnKey;
  value: string;
}

// Default assignment form state
// target_endpoint is empty by default - backend will use GRPC_TARGET_ADDRESS env var
const defaultAssignForm: IModemAssignRequest = {
  target_endpoint: '',
  modem: {
    imei: '',
    owner: '',
    email: '',
    company: '',
    phone: '',
    notes: '',
    machine_uuid: '',
    firmware_version: '',
    iccid: '',
    imsi: '',
  },
};

const ModemList: React.FC = () => {
  const [searchParams, setSearchParams] = useState<IModemListParams>({
    page: 1,
    pageSize: 25,
    q: '',
    sort: '',
  });

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newModem, setNewModem] = useState<Partial<IModem>>({ modem_id: '' });

  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState<IModemAssignRequest>(defaultAssignForm);
  const [assigningModem, setAssigningModem] = useState<IModem | null>(null);

  const { data, isLoading, error, isFetching, refetch } = useModems(searchParams);
  const updateMutation = useUpdateModem();
  const deleteMutation = useDeleteModem();
  const createMutation = useCreateModem();
  const assignMutation = useAssignModem();

  // Search handler
  const handleSearch = useCallback((query: string) => {
    setSearchParams(prev => ({ ...prev, q: query, page: 1 }));
  }, []);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setSearchParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Sort handler
  const handleSort = useCallback((field: string) => {
    setSearchParams(prev => {
      const currentSort = prev.sort || '';
      let newSort = field;
      if (currentSort === field) newSort = `-${field}`;
      else if (currentSort === `-${field}`) newSort = '';
      return { ...prev, sort: newSort, page: 1 };
    });
  }, []);

  const getSortIcon = (field: string) => {
    const currentSort = searchParams.sort || '';
    if (currentSort === field) return ' ↑';
    if (currentSort === `-${field}`) return ' ↓';
    return '';
  };

  // Start editing a cell
  const startEditing = (modem: IModem, field: ColumnKey) => {
    const column = COLUMNS.find(c => c.key === field);
    if (!column?.editable) return;
    setEditingCell({
      modemId: modem.modem_id,
      field,
      value: modem[field] || '',
    });
  };

  // Save edited cell
  const saveEdit = async () => {
    if (!editingCell) return;

    try {
      await updateMutation.mutateAsync({
        modemId: editingCell.modemId,
        data: { [editingCell.field]: editingCell.value },
      });
      setEditingCell(null);
    } catch (err) {
      logger.error('Failed to update:', err);
      alert('Failed to save changes');
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingCell(null);
  };

  // Handle key press in edit mode
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Delete a modem
  const handleDelete = async (modem: IModem) => {
    if (!window.confirm(`Delete modem "${modem.modem_id}"?`)) return;
    try {
      await deleteMutation.mutateAsync(modem.modem_id);
    } catch (err) {
      logger.error('Failed to delete:', err);
      alert('Failed to delete modem');
    }
  };

  // Add new modem
  const handleAddModem = async () => {
    if (!newModem.modem_id?.trim()) {
      alert('Part Number is required');
      return;
    }
    try {
      await createMutation.mutateAsync({
        modem_id: newModem.modem_id,
        serial_number: newModem.serial_number,
        imei: newModem.imei,
        firmware_version: newModem.firmware_version,
        owner: newModem.owner,
        company: newModem.company,
        email: newModem.email,
        phone: newModem.phone,
        notes: newModem.notes,
        assigned_at: newModem.assigned_at,
        installed_sim: newModem.installed_sim,
      });
      setNewModem({ modem_id: '' });
      setShowAddRow(false);
    } catch (err) {
      logger.error('Failed to create:', err);
      alert('Failed to create modem');
    }
  };

  // Open assign modal for a modem
  const handleOpenAssignModal = (modem: IModem) => {
    setAssigningModem(modem);
    setAssignForm({
      target_endpoint: '', // Use backend's GRPC_TARGET_ADDRESS env var
      modem: {
        imei: modem.imei || '',
        firmware_version: modem.firmware_version || '',
        iccid: modem.installed_sim || '',
        imsi: '',
        owner: modem.owner || '',
        email: modem.email || '',
        company: modem.company || '',
        phone: modem.phone || '',
        notes: modem.notes || '',
        machine_uuid: '',
      },
    });
    setShowAssignModal(true);
  };

  // Close assign modal
  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssigningModem(null);
    setAssignForm(defaultAssignForm);
  };

  // Submit assignment
  const handleAssignModem = async () => {
    // target_endpoint is optional - backend will use GRPC_TARGET_ADDRESS env var if empty
    if (!assignForm.modem.imei?.trim()) {
      alert('IMEI is required');
      return;
    }
    if (!assignForm.modem.owner?.trim()) {
      alert('Owner is required');
      return;
    }

    try {
      const result = await assignMutation.mutateAsync(assignForm);
      if (result.success) {
        alert(`Assignment successful: ${result.message}`);
        handleCloseAssignModal();
      } else {
        alert(`Assignment failed: ${result.message}`);
      }
    } catch (err) {
      logger.error('Failed to assign:', err);
      alert(`Failed to assign modem: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Update assign form field
  const updateAssignForm = (field: keyof IModemAssignRequest['modem'], value: string) => {
    setAssignForm(prev => ({
      ...prev,
      modem: { ...prev.modem, [field]: value },
    }));
  };

  // Render cell content
  const renderCell = (modem: IModem, column: typeof COLUMNS[number]) => {
    const isEditing = editingCell?.modemId === modem.modem_id && editingCell?.field === column.key;
    const value = modem[column.key] || '';

    if (isEditing) {
      return (
        <input
          type="text"
          value={editingCell.value}
          onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
          onBlur={saveEdit}
          onKeyDown={handleKeyPress}
          autoFocus
          className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      );
    }

    return (
      <div
        onClick={() => column.editable && startEditing(modem, column.key)}
        className={`px-2 py-1 ${column.editable ? 'cursor-pointer hover:bg-blue-50 rounded' : ''} ${!value ? 'text-gray-400 dark:text-gray-500 italic' : ''}`}
        title={column.editable ? 'Click to edit' : undefined}
      >
        {value || (column.editable ? 'Click to edit' : '-')}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load modems'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Modem Management</h1>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
            {isFetching && <LoadingSpinner size="small" />}
            <span className="whitespace-nowrap">{data?.total_count || 0} modems</span>
          </span>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 min-h-[44px] min-w-[44px]"
            title="Refresh"
          >
            <svg className={`h-5 w-5 text-gray-600 dark:text-gray-400 ${isFetching ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={() => setShowAddRow(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded min-h-[44px] text-sm sm:text-base whitespace-nowrap"
          >
            <span className="sm:hidden">+ Add</span>
            <span className="hidden sm:inline">Add Modem</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchBar
        value={searchParams.q || ''}
        onChange={handleSearch}
        placeholder="Search modems..."
      />

      {/* Page Size */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700 dark:text-gray-300">Per page:</span>
        <select
          value={searchParams.pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded px-2 py-1 text-sm min-h-[36px]"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Mobile Add Form */}
      {showAddRow && (
        <div className="lg:hidden bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-3">
          <h3 className="font-medium text-gray-900 dark:text-white">Add New Modem</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {COLUMNS.slice(0, 6).map((col) => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {col.label} {col.key === 'modem_id' && '*'}
                </label>
                <input
                  type="text"
                  value={newModem[col.key] || ''}
                  onChange={(e) => setNewModem({ ...newModem, [col.key]: e.target.value })}
                  placeholder={col.label}
                  className={`w-full px-3 py-2 border rounded-md text-sm min-h-[44px] ${col.key === 'modem_id' ? 'border-blue-500' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-800'}`}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleAddModem}
              disabled={createMutation.isLoading}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md min-h-[44px] font-medium"
            >
              {createMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => { setShowAddRow(false); setNewModem({ modem_id: '' }); }}
              className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md min-h-[44px]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {(!data?.modems || data.modems.length === 0) && !showAddRow ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No modems found. Click "Add Modem" to create one.
          </div>
        ) : (
          data?.modems?.map((modem) => (
            <MobileCard
              key={modem.modem_id}
              title={modem.modem_id}
              subtitle={modem.serial_number || modem.imei || 'No serial/IMEI'}
              status={
                modem.owner ? (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    Assigned
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    Unassigned
                  </span>
                )
              }
              fields={[
                { label: 'IMEI', value: modem.imei || '-' },
                { label: 'Owner', value: modem.owner || '-' },
                { label: 'Company', value: modem.company || '-' },
                { label: 'Firmware', value: modem.firmware_version || '-' },
                ...(modem.installed_sim ? [{ label: 'SIM', value: modem.installed_sim }] : []),
              ]}
              actions={[
                {
                  label: 'Assign',
                  onClick: () => handleOpenAssignModal(modem),
                  variant: 'primary' as const,
                  disabled: assignMutation.isLoading,
                },
                {
                  label: 'Delete',
                  onClick: () => handleDelete(modem),
                  variant: 'danger' as const,
                  disabled: deleteMutation.isLoading,
                },
              ]}
            />
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    {col.label}{getSortIcon(col.key)}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Add New Row */}
              {showAddRow && (
                <tr className="bg-green-50 dark:bg-green-900/20">
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-4 py-2">
                      <input
                        type="text"
                        value={newModem[col.key] || ''}
                        onChange={(e) => setNewModem({ ...newModem, [col.key]: e.target.value })}
                        placeholder={col.key === 'modem_id' ? 'Required' : col.label}
                        className={`w-full px-2 py-1 border rounded text-sm ${col.key === 'modem_id' ? 'border-blue-500' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-800'}`}
                      />
                    </td>
                  ))}
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddModem}
                        disabled={createMutation.isLoading}
                        className="text-green-600 hover:text-green-800 font-medium text-sm"
                      >
                        {createMutation.isLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => { setShowAddRow(false); setNewModem({ modem_id: '' }); }}
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Data Rows */}
              {data?.modems?.map((modem) => (
                <tr key={modem.modem_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {renderCell(modem, col)}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenAssignModal(modem)}
                        disabled={assignMutation.isLoading}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Assign
                      </button>
                      <button
                        onClick={() => handleDelete(modem)}
                        disabled={deleteMutation.isLoading}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Empty State */}
              {(!data?.modems || data.modems.length === 0) && !showAddRow && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No modems found. Click "Add Modem" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <Pagination
          currentPage={searchParams.page || 1}
          totalPages={data.total_pages}
          totalItems={data.total_count}
          pageSize={searchParams.pageSize || 25}
          onPageChange={handlePageChange}
        />
      )}

      {/* Edit hint */}
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        Click on any cell to edit. Press Enter to save or Escape to cancel.
      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg p-4 sm:p-6 w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Assign Modem to Customer
            </h2>
            {assigningModem && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                Modem: {assigningModem.modem_id}
                <span className="hidden xs:inline"> (IMEI: {assigningModem.imei || 'N/A'})</span>
              </p>
            )}

            <div className="space-y-4">
              {/* Target Endpoint */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Target gRPC Endpoint
                </label>
                <input
                  type="text"
                  value={assignForm.target_endpoint}
                  onChange={(e) => setAssignForm(prev => ({ ...prev, target_endpoint: e.target.value }))}
                  placeholder="Leave empty to use server default (GRPC_TARGET_ADDRESS)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Optional: Uses GRPC_TARGET_ADDRESS env var if empty</p>
              </div>

              {/* IMEI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  IMEI *
                </label>
                <input
                  type="text"
                  value={assignForm.modem.imei}
                  onChange={(e) => updateAssignForm('imei', e.target.value)}
                  placeholder="IMEI"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Owner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Owner *
                </label>
                <input
                  type="text"
                  value={assignForm.modem.owner}
                  onChange={(e) => updateAssignForm('owner', e.target.value)}
                  placeholder="Owner name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={assignForm.modem.email}
                  onChange={(e) => updateAssignForm('email', e.target.value)}
                  placeholder="owner@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={assignForm.modem.company}
                  onChange={(e) => updateAssignForm('company', e.target.value)}
                  placeholder="Company name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={assignForm.modem.phone}
                  onChange={(e) => updateAssignForm('phone', e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Machine UUID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Machine UUID
                </label>
                <input
                  type="text"
                  value={assignForm.modem.machine_uuid}
                  onChange={(e) => updateAssignForm('machine_uuid', e.target.value)}
                  placeholder="e.g., 101-250901-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* ICCID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SIM ICCID
                </label>
                <input
                  type="text"
                  value={assignForm.modem.iccid}
                  onChange={(e) => updateAssignForm('iccid', e.target.value)}
                  placeholder="SIM card ICCID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={assignForm.modem.notes}
                  onChange={(e) => updateAssignForm('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 xs:gap-3 mt-4 sm:mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseAssignModal}
                disabled={assignMutation.isLoading}
                className="w-full xs:w-auto px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignModem}
                disabled={assignMutation.isLoading}
                className="w-full xs:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 min-h-[44px] font-medium"
              >
                {assignMutation.isLoading ? 'Assigning...' : 'Assign Modem'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModemList;
